/**
 * Survive a deploy that happens while someone is mid-session.
 *
 * Vite content-hashes every code-split chunk, so a redeploy replaces them all.
 * A tab that already has the old `index.html` will ask for a chunk filename
 * that no longer exists, the dynamic `import()` rejects, `React.lazy` throws,
 * and the router lands on its error boundary — a perfectly working app showing
 * "Off the hanger" to anyone who happened to be browsing during a deploy.
 *
 * The page's own HTML is the stale part, so the fix is to reload once and pick
 * up the new asset manifest. `sessionStorage` bounds it to a single attempt per
 * chunk: if the chunk is genuinely missing (a bad build, an offline user), the
 * second failure is allowed through to the error boundary rather than trapping
 * the tab in a reload loop.
 */

const RETRY_PREFIX = 'racksavant.chunk-retry:';

/** Chrome, Firefox and Safari all word this differently. */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|ChunkLoadError/i.test(
    message,
  );
}

export function lazyChunk<T>(key: string, load: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      const result = await load();
      // A later deploy should get its own retry, so clear on success.
      sessionStorage.removeItem(RETRY_PREFIX + key);
      return result;
    } catch (error) {
      if (!isStaleChunkError(error) || typeof window === 'undefined') throw error;

      const storageKey = RETRY_PREFIX + key;
      if (sessionStorage.getItem(storageKey)) throw error;
      sessionStorage.setItem(storageKey, '1');

      // Hard reload: a normal one can still be served the cached HTML.
      window.location.reload();
      // Never settles — the document is being torn down.
      return new Promise<T>(() => {});
    }
  };
}
